import styles from './PlaylistStats.module.css'

import lodashSumBy from 'lodash/sumBy'
import React, { useCallback, useMemo } from 'react'

import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type {
  Playlist,
  PlaylistStatsByUser,
  PlaylistTrackStats,
} from 'types/playlist'

import PlaylistStatsSummary from 'components/common/playlist-stats-summary'
import StatsTableEmpty from 'components/common/stats-table/stats-table-empty'
import HelpTooltip from 'components/common/tooltip'
import Alert from 'components/ui/alert'
import type { Column } from 'components/ui/data-table'
import DataTable from 'components/ui/data-table'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import Link from 'components/ui/link'
import ModalWindow from 'components/ui/modal-window'
import PricingBadge from 'components/ui/pricing-badge'
import Spinner from 'components/ui/spinner'
import Stack from 'components/ui/stack'
import TextOverflow from 'components/ui/text-overflow'
import Tooltip from 'components/ui/tooltip'
import UserAvatar from 'components/ui/user-avatar'

import { useDispatch, useSelector } from 'hooks/use-redux'

const DEFAULT_SORT = {
  col: 0,
  dir: 1,
} as const // A-Z, sort anon to last

enum ColumnType {
  NUMERIC = 'numeric',
  STRING = 'string',
  DATE = 'date',
}

enum ColumnWidth {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  CONTENT = 'content',
}

type TAggregatedEventsEnum = 'track_downloads' | 'track_streams'
type Props = {
  isFeatureEnabled?: boolean
  isTrial: boolean
  playlist: Playlist
  onClose?: () => void
}

const PlaylistStats = ({
  isFeatureEnabled,
  isTrial,
  playlist,
  onClose,
}: Props) => {
  const dispatch = useDispatch()
  const isArtist = useFeatureEnabled(IS_ARTIST)
  const isPhone = useSelector(isDeviceTypePhone)
  const isEmpty: boolean = useMemo(() => {
    if (!playlist) {
      return true
    }

    const { stats_all, stats_by_user } = playlist

    if (stats_all && stats_by_user) {
      return (
        stats_by_user.length +
          stats_all.playlist_accesses +
          stats_all.playlist_downloads +
          stats_all.track_downloads +
          stats_all.track_streams ===
        0
      )
    } else {
      return true
    }
  }, [playlist])
  const handleContactsLinkClick = useCallback(() => {
    dispatch(sendMixpanelEvent('Access Contacts via playlist stats CTA'))
  }, [dispatch])
  const handleUserClick = useCallback(() => {
    dispatch(sendMixpanelEvent('Access Contacts via playlist stats contact'))
  }, [dispatch])

  const renderCell = (
    { stats, track_stats }: PlaylistStatsByUser,
    label: string,
    aggregatedEventType: TAggregatedEventsEnum,
  ) => {
    let popover = (
      <HelpTooltip
        base={
          <div className={styles.playlistStatsPopover}>
            {lodashSumBy(track_stats[aggregatedEventType], 'count')}
          </div>
        }
        customScrollbars
      >
        <div>
          <b>{label}</b>
        </div>
        <div>
          <br />
        </div>
        {track_stats[aggregatedEventType].map(
          (item: PlaylistTrackStats, index: number) => (
            <div key={index}>
              <span>
                <span>
                  {item.track.name ? item.track.name : 'Untitled Track'}
                </span>
                <span> - </span>
                {item.count}
              </span>
            </div>
          ),
        )}
      </HelpTooltip>
    )

    if (!isFeatureEnabled) {
      popover = (
        <div className={styles.playlistStatsPopover}>
          <Tooltip
            content={
              <>
                Upgrade to see which tracks were streamed
                <br />
                and how many times.
                <br />
                <PricingBadge title="PLUS" />
              </>
            }
          >
            {stats[aggregatedEventType]}
          </Tooltip>
        </div>
      )
    }

    return track_stats[aggregatedEventType].length === 0
      ? stats[aggregatedEventType]
      : popover
  }

  const renderUser = (user: PlaylistStatsByUser['user']) => {
    const username =
      user.id !== null
        ? user.name || user.email || 'Unnamed share'
        : 'Other recipients'
    return user.contact_id ? (
      <Link
        className={styles.playlistStatsUser}
        href={`/contacts/${user.contact_id}/activity`}
        onClick={handleUserClick}
      >
        <UserAvatar
          className={styles.playlistStatsUserAvatar}
          name={username}
          size="24px"
          fontSize="11px"
        />
        <div className={styles.playlistStatsUserCaption}>
          <TextOverflow content={username} />
        </div>
      </Link>
    ) : (
      <span className={styles.playlistStatsUser}>
        <UserAvatar
          className={styles.playlistStatsUserAvatar}
          size="24px"
          fontSize="11px"
        />
        <div className={styles.playlistStatsUserCaption}>
          <TextOverflow content={username} />
        </div>
      </span>
    )
  }

  const columns: Column[] = useMemo(
    () => [
      {
        name: 'User name',
        toSortable: ({ user }: PlaylistStatsByUser) => {
          if (!user.name && !user.email) {
            return null
          } else {
            return user.name || user.email
          }
        },
        width: ColumnWidth.LARGE,
        render: ({ user }: PlaylistStatsByUser) => renderUser(user),
      },
      {
        id: 'stats.playlist_accesses',
        name: 'Page views',
        type: ColumnType.NUMERIC,
        width: ColumnWidth.CONTENT,
      },
      {
        id: 'stats.playlist_downloads',
        name: 'Playlist downloads',
        type: ColumnType.NUMERIC,
        width: ColumnWidth.CONTENT,
      },
      {
        name: 'Track downloads',
        toSortable: (props: Record<string, any>) => props.stats.track_downloads,
        type: ColumnType.NUMERIC,
        width: ColumnWidth.CONTENT,
        render: (props: Record<string, any>) =>
          renderCell(props as any, 'Track downloads', 'track_downloads'),
      },
      {
        name: 'Streams',
        toSortable: (props: Record<string, any>) => props.stats.track_streams,
        type: ColumnType.NUMERIC,
        width: ColumnWidth.CONTENT,
        render: (props: Record<string, any>) =>
          renderCell(props as any, 'Streams', 'track_streams'),
      },
    ],
    [renderCell, renderUser],
  )

  const renderEmptyState = () => {
    // TODO: add a link to Share when there will be one
    const sharingYourList = (
      <InlineLink to={`/explorer/playlists/${playlist.playlist_id}/share`}>
        sharing your list.
      </InlineLink>
    )
    return (
      <StatsTableEmpty cta={<>Generate some activity by {sharingYourList}</>} />
    )
  }

  const renderContents = () => {
    const { stats_all, stats_by_user } = playlist

    if (!stats_all || isEmpty) {
      return renderEmptyState()
    }

    return isPhone ? (
      <Stack space="medium">
        <PlaylistStatsSummary stats={stats_all} />
        <>
          <Heading size={500}>User activity</Heading>
          <DataTable
            columns={columns}
            defaultSort={DEFAULT_SORT}
            data={stats_by_user}
          />
        </>
      </Stack>
    ) : (
      <DataTable
        columns={[
          { ...columns[0], name: 'Total' },
          {
            ...columns[1],
            caption: false,
            name: `Page views ${stats_all.playlist_accesses}`,
            width: 'small',
          },
          {
            ...columns[2],
            caption: false,
            name: `Playlist downloads ${stats_all.playlist_downloads}`,
            width: 'small',
          },
          {
            ...columns[3],
            caption: false,
            name: `Track downloads ${stats_all.track_downloads}`,
            width: 'small',
          },
          {
            ...columns[4],
            caption: false,
            name: `Streams ${stats_all.track_streams}`,
            width: 'small',
          },
        ]}
        defaultSort={DEFAULT_SORT}
        data={stats_by_user}
      />
    )
  }

  return (
    <ModalWindow
      header={
        <Heading>
          {playlist
            ? `Stats for ${playlist.name || 'untitled playlist'}`
            : 'Playlist Stats'}
        </Heading>
      }
      footer={
        !isEmpty && (
          <>
            To view more activity for your recipients, and manage their details,
            visit{' '}
            <InlineLink to="/contacts/list" onClick={handleContactsLinkClick}>
              Contacts
            </InlineLink>
            .
          </>
        )
      }
      isOpen
      onClose={onClose}
    >
      <Stack space="medium">
        {(!isFeatureEnabled || isTrial) && !isArtist && (
          <Alert icon="warning" status="warning">
            {isTrial ? (
              <>
                Full playlist stats, including which tracks were played and
                downloaded and how many times, are available on&nbsp;
                <InlineLink to="/settings/billing">
                  Plus and Pro plans.
                </InlineLink>
              </>
            ) : (
              <>
                Want more stats, like which tracks were streamed and downloaded,
                and how many times?&nbsp;
                <PricingBadge title="upgrade" />
              </>
            )}
          </Alert>
        )}
        {playlist?.has_stats_loaded ? renderContents() : <Spinner />}
      </Stack>
    </ModalWindow>
  )
}

export default React.memo<Props>(PlaylistStats)
